import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Icon,
  FormField,
  Select,
  Textarea,
  Button,
  TableProps,
  SelectProps,
} from "@cloudscape-design/components-themed";
import { COMMON_LABELS } from "../default-constants";
import {
  COMMENT_MAX_LENGTH,
  IOrderTargetItems,
  LABELS,
  V_M_BRANDS,
} from "./constants";
import TableEmptyState from "../UI/TableEmptyState";
import { useState } from "react";
import {
  getOrderTargetsSortedByDate,
  isAfterJudgement,
  isDuplication,
} from "./util";
import { useAppDispatch } from "../../app/hook";
import { showModal } from "../UI/modal/modal-slice";
import { ReexaminationOrdersApplicationRequestItem } from "../../features/api-interfaces";
import { addNotification } from "../../features/error/notifications-slice";
import { usePutReexaminationOrdersApplicationMutation } from "../../features/api";
import { getMessage } from "../../features/error/messages";
import { isLengthValid } from "../../utils/inputValidation";

interface IReexaminationInstruction {
  examinationApplicationNo: string;
  isStore: boolean;
  columnDefinitions: TableProps.ColumnDefinition<IOrderTargetItems>[];
  items: IOrderTargetItems[];
  backOnClick: Function;
}

/**
 * 再審査対象選定・指示
 */
const ReexaminationInstruction = (props: IReexaminationInstruction) => {
  const {
    examinationApplicationNo,
    isStore,
    columnDefinitions,
    items,
    backOnClick,
  } = props;
  const dispatch = useAppDispatch();
  const [putReexaminationOrdersApplication] =
    usePutReexaminationOrdersApplicationMutation();
  const [reexaminationReason, setReexaminationReason] = useState("");
  const [reexaminationReasonErrorMsg, setReexaminationReasonErrorMsg] =
    useState("");
  const [selectedVMBrand, setSelectedVMBrand] = useState<SelectProps.Option>({
    value: V_M_BRANDS[0].value,
    label: V_M_BRANDS[0].label,
  });
  const sortedItems = getOrderTargetsSortedByDate(items);
  const isAfterJudge = isAfterJudgement(items);

  /**
   * 再審査理由の入力ハンドラ.
   * @param {string} inputComment 入力コメント
   */
  const reexaminationReasonOnChange = (inputComment: string) => {
    setReexaminationReason(inputComment);
    const errorMsg = isLengthValid(inputComment, COMMENT_MAX_LENGTH)
      ? ""
      : getMessage("KGRERR058", 500).label;
    setReexaminationReasonErrorMsg(errorMsg);
  };

  /**
   * 申請ボタンを押したときの処理.
   */
  const applicationOnClick = () => {
    if (!isLengthValid(reexaminationReason, COMMENT_MAX_LENGTH)) {
      return;
    }
    if (isDuplication(selectedVMBrand.label!, sortedItems)) {
      dispatch(
        addNotification({
          msgId: "KGRERR086",
          msgArgs: [],
        })
      );
    } else {
      dispatch(
        showModal({
          content: COMMON_LABELS.CONTENTS.APPLICATION,
          positiveFn: () => {
            const requestData: ReexaminationOrdersApplicationRequestItem = {
              examinationApplicationNo,
              data: {
                applicationBrand: Number(selectedVMBrand.value),
                reexaminationReason,
              },
            };
            putReexaminationOrdersApplication(requestData).then(
              (result: any) => {
                if ("error" in result && "data" in result.error) {
                  const { messageId, errorDetail } = result.error.data;
                  if (messageId) {
                    dispatch(
                      addNotification({
                        msgId: messageId,
                        msgArgs: errorDetail ?? [],
                      })
                    );
                  }
                } else {
                  backOnClick();
                }
              }
            );
          },
        })
      );
    }
  };

  return (
    <Container header={<Header>{LABELS.SUB_TITLE_LEFT}</Header>}>
      <SpaceBetween direction={"vertical"} size={"s"}>
        <Table
          columnDefinitions={columnDefinitions}
          items={sortedItems}
          empty={<TableEmptyState />}
          resizableColumns
        />
        <Box textAlign={"center"}>
          <Icon name={"caret-down-filled"} />
        </Box>
        <FormField label={LABELS.EXAMINATION_TARGET}>
          <SpaceBetween direction={"horizontal"} size={"xxl"}>
            {LABELS.V_M}
            <Select
              selectedOption={selectedVMBrand}
              options={V_M_BRANDS}
              onChange={({ detail }) => {
                setReexaminationReason("");
                setSelectedVMBrand(detail.selectedOption);
              }}
              disabled={isStore || isAfterJudge}
            />
          </SpaceBetween>
        </FormField>
        <SpaceBetween direction={"vertical"} size={"xxs"}>
          <FormField
            label={LABELS.RE_EXAMINATION_REASON}
            constraintText={`${COMMENT_MAX_LENGTH}文字以内`}
            errorText={reexaminationReasonErrorMsg}
          >
            <Textarea
              value={reexaminationReason}
              onChange={({ detail }) =>
                reexaminationReasonOnChange(detail.value)
              }
              rows={2}
              disabled={isStore || isAfterJudge}
            />
          </FormField>
        </SpaceBetween>
        <Box textAlign={"right"}>
          <Button
            formAction={"none"}
            variant={"normal"}
            onClick={applicationOnClick}
            disabled={isStore || isAfterJudge}
          >
            {COMMON_LABELS.BTNS.APPLY}
          </Button>
        </Box>
      </SpaceBetween>
    </Container>
  );
};

export default ReexaminationInstruction;
